import { isWithCode } from '@orionprotocol/utils';
import { coinbaseWallet } from '@wagmi/connectors';
import {
  connect,
  ConnectorNotFoundError,
  createConfig,
  disconnect,
  getConnections,
  reconnect,
  switchChain,
  watchAccount,
  watchChainId,
} from '@wagmi/core';
import { JsonRpcSigner } from 'ethers';

import { ConnectSuccessData, GetWalletAPI } from '../WalletAPI';
import {
  WalletAPIError,
  WalletChangeNetworkFailed,
  WalletConnectCancelled,
  WalletDisconnectFailed,
} from '../walletAPIErrors';
import { Logger } from '../Logger';
import { getEthersSigner } from '../ethersAdapters';
import { availableChains, transports } from '../availableChains';

export function createCoinbaseAPI({
  appName = '',
  logger = console,
}: {
  appName?: string;
  logger?: Logger;
} = {}): GetWalletAPI {
  const config = createConfig({
    chains: availableChains,
    connectors: [coinbaseWallet({ appName })],
    transports,
  });

  return ({ type, onAccountChanged, onChainChanged, onDisconnected }) => {
    const unwatchAccounts = watchAccount(config, {
      onChange(data) {
        onAccountChanged?.({
          address: data.address,
          chainId: data.chainId,
        });
      },
    });

    const unwatchChainId = watchChainId(config, {
      onChange(chainId) {
        onChainChanged?.({
          chainId,
        });
      },
    });

    return {
      type,
      getSigner: (): Promise<JsonRpcSigner | undefined> =>
        getEthersSigner(config),
      connect: async (): Promise<ConnectSuccessData> => {
        try {
          const connections = getConnections(config);

          if (connections.length !== 0) {
            const reconnectData = await reconnect(config);
            return {
              address: reconnectData?.[0]?.accounts[0].toLowerCase(),
              chainId: reconnectData?.[0]?.chainId,
            };
          }

          const { accounts, chainId } = await connect(config, {
            connector: coinbaseWallet({ appName }),
          });
          return {
            address: accounts[0].toLowerCase(),
            chainId,
          };
        } catch (e) {
          if (e instanceof ConnectorNotFoundError) {
            throw new Error('Please install Coinbase extension to use it.');
          }

          const code = isWithCode(e) ? e.code : undefined;

          if (code === 4001) {
            throw new WalletConnectCancelled(e, logger);
          }

          throw new WalletAPIError(e, logger);
        }
      },
      disconnect: async (): Promise<void> => {
        try {
          await disconnect(config);
          unwatchAccounts();
          unwatchChainId();
          onDisconnected?.();
        } catch (e) {
          throw new WalletDisconnectFailed(e, logger);
        }
      },
      changeNetwork: async ({
        chainId,
      }: {
        chainId: string;
      }): Promise<void> => {
        try {
          await switchChain(config, { chainId: Number(chainId) });
        } catch (e) {
          throw new WalletChangeNetworkFailed(e, logger);
        }
      },
    };
  };
}
